// Precaches the app shell + public/card-art/* + public/board/* (see
// vue.config.js's `pwa.workboxOptions` for exactly what's included/excluded)
// so local hotseat/solo-vs-AI play works with zero network - Matt: play on
// an iPad on a flight, after having visited the site once while online.
// Remote multiplayer still needs a live connection (inherent to syncing two
// devices) - this is only about the app shell, card art, and local play
// itself being available offline.
//
// Deliberately hand-rolled against the raw `navigator.serviceWorker` API
// rather than pulling in the `register-service-worker` npm package that
// `vue add pwa`'s generator would normally scaffold - the only two things
// actually needed here (register once, and let checkForUpdate.js nudge an
// update check) are a handful of lines, and this repo doesn't add a
// dependency for something that small (see CLAUDE.md's own "no speculative
// shared infra" stance for the sibling wrapper repo - same spirit here).
//
// No-ops entirely outside a real browser (vitest/jsdom) and in dev
// (`yarn serve`) - a service worker is only emitted by `vue-cli-service
// build` (see vue.config.js: workbox's plugin is production-only), so
// registering it during `yarn serve` would just 404.
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.BASE_URL || '/'}service-worker.js`).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('Service worker registration failed:', err.message);
    });
  });
}

// Called by appUpdate.js's reloadForUpdate() right when a newer deploy has
// been found and we're about to reload - see that file's own comment.
//
// This started as a fire-and-forget `nudgeServiceWorkerUpdate()`: kick
// registration.update() and reload a beat later, hoping the new worker won
// the race. It now actually WAITS for the install to finish, because losing
// that race isn't harmless - a reload that lands while a new worker is
// mid-install can be served a mix of old and new precached assets, which
// shows up as a blank screen rather than as "still on the old version."
// (skipWaiting/clientsClaim in vue.config.js are what make a newly-installed
// worker take over immediately once it's ready, rather than waiting for
// every old tab to close first.)
//
// Bounded, and every failure is swallowed: the reload must happen either
// way. Worst case that reload picks up the new build on its next check
// instead of this one.
export async function waitForNewWorker(timeoutMs = 15000) {
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.();
    if (!registration) return;
    await registration.update?.().catch(() => {});
    const deadline = Date.now() + timeoutMs;
    while ((registration.installing || registration.waiting) && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 250); });
    }
  } catch {
    // No service worker support at all, or any surprise from the API - must
    // never block the reload this exists to precede.
  }
}
